import React from 'react'
import { PAGE_BG, AppearanceMode } from 'src/theme/appCssVars'

export type AppearancePreference = 'light' | 'dark' | 'system'
export type ResolvedMode = AppearanceMode

export const APPEARANCE_STORAGE_KEY = 'appearance'
// The pre-glass toggle ("original dark nav" vs "light glass"). Both values
// meant a light content area, so anyone who touched it stays on light.
const LEGACY_LEFT_NAV_THEME_KEY = 'leftNavTheme'
// Flip to 'system' once the dark theme has been used in production for a while.
export const DEFAULT_APPEARANCE: AppearancePreference = 'light'
const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)'

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

export const isAppearancePreference = (
  value: unknown
): value is AppearancePreference =>
  value === 'light' || value === 'dark' || value === 'system'

export const readStoredAppearance = (
  storage: StorageLike
): AppearancePreference => {
  const stored = storage.getItem(APPEARANCE_STORAGE_KEY)
  if (isAppearancePreference(stored)) return stored

  if (storage.getItem(LEGACY_LEFT_NAV_THEME_KEY) !== null) {
    storage.removeItem(LEGACY_LEFT_NAV_THEME_KEY)
    storage.setItem(APPEARANCE_STORAGE_KEY, 'light')

    return 'light'
  }

  return DEFAULT_APPEARANCE
}

export const resolveMode = (
  preference: AppearancePreference,
  systemPrefersDark: boolean
): ResolvedMode => {
  if (preference === 'system') return systemPrefersDark ? 'dark' : 'light'

  return preference
}

const canMatchMedia = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'

const getSystemPrefersDark = () =>
  canMatchMedia() ? window.matchMedia(DARK_SCHEME_QUERY).matches : false

const subscribeToSystemScheme = (onChange: () => void) => {
  if (!canMatchMedia()) return () => undefined

  const mediaQuery = window.matchMedia(DARK_SCHEME_QUERY)
  // Safari < 14 only implements the legacy addListener/removeListener pair.
  if (typeof mediaQuery.addEventListener === 'function') {
    mediaQuery.addEventListener('change', onChange)

    return () => mediaQuery.removeEventListener('change', onChange)
  }
  mediaQuery.addListener(onChange)

  return () => mediaQuery.removeListener(onChange)
}

const getInitialPreference = (): AppearancePreference => {
  try {
    return readStoredAppearance(window.localStorage)
  } catch {
    return DEFAULT_APPEARANCE
  }
}

interface AppearanceContextValue {
  preference: AppearancePreference
  resolvedMode: ResolvedMode
  setPreference: (preference: AppearancePreference) => void
}

export const AppearanceContext = React.createContext<AppearanceContextValue>({
  preference: DEFAULT_APPEARANCE,
  resolvedMode: 'light',
  setPreference: () => null,
})

export const AppearanceProvider = (props: { children: React.ReactNode }) => {
  const [preference, setPreferenceState] =
    React.useState<AppearancePreference>(getInitialPreference)
  const systemPrefersDark = React.useSyncExternalStore(
    subscribeToSystemScheme,
    getSystemPrefersDark,
    () => false
  )
  const resolvedMode = resolveMode(preference, systemPrefersDark)

  const setPreference = React.useCallback((next: AppearancePreference) => {
    setPreferenceState(next)

    try {
      window.localStorage.setItem(APPEARANCE_STORAGE_KEY, next)
    } catch {
      return
    }
  }, [])

  // Document-level hooks: the attribute drives the pre-React <style> in
  // public/index.html, Tailwind's dark variant and the ::root token block.
  React.useLayoutEffect(() => {
    document.documentElement.dataset.theme = resolvedMode
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', PAGE_BG[resolvedMode])
  }, [resolvedMode])

  const value = React.useMemo(
    () => ({ preference, resolvedMode, setPreference }),
    [preference, resolvedMode, setPreference]
  )

  return (
    <AppearanceContext.Provider value={value}>
      {props.children}
    </AppearanceContext.Provider>
  )
}

export const useAppearance = () => React.useContext(AppearanceContext)
